import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// Medical Condition Status Enum
export enum MedicalConditionStatus {
  New = 1,           // חדש
  InTreatment = 2,   // בטיפול
  Approved = 3,      // אושר
  Rejected = 4,      // נדחה
  Pending = 5        // ממתין להחלטה
}

export interface ClientMedicalInfoDto {
  id: number;
  clientId: number;
  diseaseId: number;
  diseaseName: string;
  diseaseCategoryName: string;
  diseaseType: string; // "ראשית" או "משנית"
  isPrimary: boolean;
  diagnosisDate?: string;
  notes?: string;
  physicianName?: string;
  physicianPhone?: string;
  isTimeSensitive: boolean;
  status?: number;
  statusName?: string;
  wasHospitalizedForThisDisease?: boolean;
  hadSurgeryForThisDisease?: boolean;
  submittedClaimForThisDisease?: boolean;
}

export interface CreateClientMedicalInfoDto {
  clientId: number;
  diseaseId: number;
  isPrimary: boolean;
  diagnosisDate?: string;
  notes?: string;
  physicianName?: string;
  physicianPhone?: string;
  isTimeSensitive: boolean;
  status?: number;
  wasHospitalizedForThisDisease?: boolean;
  hadSurgeryForThisDisease?: boolean;
  submittedClaimForThisDisease?: boolean;
}

export interface UpdateClientMedicalInfoDto {
  diseaseId: number;
  isPrimary: boolean;
  diagnosisDate?: string;
  notes?: string;
  physicianName?: string;
  physicianPhone?: string;
  isTimeSensitive: boolean;
  status?: number;
  wasHospitalizedForThisDisease?: boolean;
  hadSurgeryForThisDisease?: boolean;
  submittedClaimForThisDisease?: boolean;
}

// Helper to get auth token
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// API functions
export const medicalConditionsApi = {
  // Get all medical conditions for a client
  getByClientId: async (clientId: number): Promise<ClientMedicalInfoDto[]> => {
    const response = await axios.get(
      `${API_URL}/ClientMedicalInfo/client/${clientId}`,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // Create a new medical condition
  create: async (data: CreateClientMedicalInfoDto): Promise<ClientMedicalInfoDto> => {
    const response = await axios.post(
      `${API_URL}/ClientMedicalInfo`,
      data,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // Update an existing medical condition
  update: async (id: number, data: UpdateClientMedicalInfoDto): Promise<ClientMedicalInfoDto> => {
    const response = await axios.put(
      `${API_URL}/ClientMedicalInfo/${id}`,
      data,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // Delete a medical condition
  delete: async (id: number): Promise<void> => {
    await axios.delete(
      `${API_URL}/ClientMedicalInfo/${id}`,
      { headers: getAuthHeader() }
    );
  }
};

// Error handling helper
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.response?.status === 403) {
      return 'אין לך הרשאה לבצע פעולה זו';
    }
    if (error.response?.status === 404) {
      return 'המידע המבוקש לא נמצא';
    }
    if (error.response?.status === 401) {
      return 'נדרשת התחברות מחדש';
    }
    return error.message || 'שגיאה בביצוע הפעולה';
  }
  return 'שגיאה לא צפויה';
};

// Helper functions for medical condition status
export const getMedicalConditionStatusName = (status: MedicalConditionStatus): string => {
  switch (status) {
    case MedicalConditionStatus.New:
      return 'חדש';
    case MedicalConditionStatus.InTreatment:
      return 'בטיפול';
    case MedicalConditionStatus.Approved:
      return 'אושר';
    case MedicalConditionStatus.Rejected:
      return 'נדחה';
    case MedicalConditionStatus.Pending:
      return 'ממתין להחלטה';
    default:
      return 'לא ידוע';
  }
};

export const getMedicalConditionStatusColor = (status: MedicalConditionStatus): string => {
  switch (status) {
    case MedicalConditionStatus.New:
      return 'var(--primary-color)';
    case MedicalConditionStatus.InTreatment:
      return 'var(--warning-color)';
    case MedicalConditionStatus.Approved:
      return 'var(--success-color)';
    case MedicalConditionStatus.Rejected:
      return 'var(--error-color)';
    case MedicalConditionStatus.Pending:
      return '#FFA500'; // Orange
    default:
      return 'var(--text-secondary)';
  }
};
